import React, { useCallback, useMemo, useState } from 'react';
import type { ColDef, EditableCallbackParams } from 'ag-grid-community';
import { Search } from 'lucide-react';
import { LopCrudPage } from '../../components/LopCrudPage';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { LopSearchModal } from '../../components/LopSearchModal';
import {
  getIdxCompositions,
  saveIdxCompositionsBatch,
} from '../../api/idxCompositions';
import type { IdxComposition } from '../../types/IdxComposition';
import { useIndices } from '../../hooks/useIndices';
import { t } from '../../i18n/t';
import './IdxCompositionsPage.css';

// "הרכב מדדים" — current-day composition data per index (Central).
// Composite natural PK (idxId, assetId) — both editable only on Add, per the
// composite-PK recipe (see CalcMethodMapPage / IdxRatesHistoryPage). No
// dropdown on assetId — ASSETS has 65K rows, same "plain numeric" call as
// AssetsPage.idxId.
//
// Index picker (DDL + magnifier) above the grid: picking an index scopes the
// grid to that index's composition only (server-side filter); no selection =
// all rows (unfiltered, today's behavior). Filters directly by our internal
// idxId — unlike TaseIdxCompositionsPage there is no taseIdxId resolution
// step, so this applies on change (no Fetch button; nothing else to combine
// it with). Same filter->getRows recipe as Inventory's `acc` / the Tase
// page's idx picker: changing idxId gives useCrudGrid a new getRows/saveBatch
// identity, which re-fetches/re-scopes server-side. No dirty-guard on the
// picker itself — mirrors Inventory (acc) and the Tase page (Fetch), neither
// of which prompts on unsaved changes when the filter changes.
const editableOnAdd = (params: EditableCallbackParams<IdxComposition>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

const columnDefs: ColDef<IdxComposition>[] = [
  {
    field: 'idxId',
    headerName: t('idxCompositions.columns.idxId'),
    width: 130,
    editable: editableOnAdd,
    cellDataType: 'number',
    sort: 'asc',
  },
  {
    field: 'assetId',
    headerName: t('idxCompositions.columns.assetId'),
    width: 150,
    editable: editableOnAdd,
    cellDataType: 'number',
  },
  {
    field: 'assetPercent',
    headerName: t('idxCompositions.columns.assetPercent'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'factor',
    headerName: t('idxCompositions.columns.factor'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
];

const newIdxCompositionDefaults = (): Partial<IdxComposition> => ({
  idxId: 0,
  assetId: 0,
  assetPercent: null,
  factor: null,
});

export function IdxCompositionsPage() {
  const { data: indices } = useIndices();

  const [idxId, setIdxId] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const idxOptions = useMemo(
    () => (indices ?? []).map((i) => ({ id: i.idxId, name: i.idxName })),
    [indices],
  );

  const getRows = useCallback(
    () => getIdxCompositions(idxId ?? undefined),
    [idxId],
  );

  const saveBatch = useCallback(
    (
      added: IdxComposition[],
      modified: IdxComposition[],
      deletedIdKeys: unknown[],
    ) =>
      saveIdxCompositionsBatch(
        added,
        modified,
        deletedIdKeys as string[],
        idxId ?? undefined,
      ),
    [idxId],
  );

  return (
    <div className="lop-history-page">
      <div className="idx-compositions__filter">
        <label
          className="idx-compositions__field idx-compositions__field--idx"
          htmlFor="idx-compositions-idx"
        >
          <span>{t('idxCompositions.filter.idx')}</span>
          <div className="idx-compositions__idx-picker">
            <LopSearchableSelect
              id="idx-compositions-idx"
              value={idxId}
              options={idxOptions}
              onChange={setIdxId}
              allowNone
              noneLabel={t('idxCompositions.filter.all')}
              placeholder={t('idxCompositions.filter.all')}
            />
            <button
              type="button"
              className="idx-compositions__idx-pick"
              aria-label={t('idxCompositions.filter.search')}
              title={t('idxCompositions.filter.search')}
              onClick={() => setPickerOpen(true)}
            >
              <Search size={16} />
            </button>
          </div>
        </label>
      </div>
      <LopCrudPage<IdxComposition>
        resourceCode="idxCompositions"
        titleKey="idxCompositions.pageTitle"
        idField="idKey"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newIdxCompositionDefaults}
      />
      {pickerOpen && (
        <LopSearchModal
          title={t('idxCompositions.filter.search')}
          table="INDICES"
          columns={[
            { field: 'idxId', headerName: t('idx.columns.idxId') },
            { field: 'idxName', headerName: t('idx.columns.idxName') },
            { field: 'symbol', headerName: t('idx.columns.symbol') },
            { field: 'taseIdxId', headerName: t('idx.columns.taseIdxId') },
          ]}
          onSelect={(row) => {
            const picked = row.idxId;
            if (typeof picked === 'number') setIdxId(picked);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
